import React, { useState } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import { Modal, Typography, Descriptions, Divider, Button, Space, Tag, Row, Col, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const { Title, Text, Paragraph } = Typography;

const LessonPlanView = ({ visible, onClose, lessonPlan, classInfo, user }) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Handle responsive layout
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  const formatDate = (date) => {
    try {
      if (!date) return '-';
      
      const momentDate = moment(date);
      if (!momentDate.isValid()) return '-';
      
      return momentDate.format('DD MMM YYYY').replace(/[a-z]/g, letter => letter.toUpperCase());
    } catch (error) {
      console.error("Error formatting date:", error);
      return '-';
    }
  };

  const formatTime = (time) => {
    try {
      if (!time) return '-';
      return moment(time, 'HH:mm:ss').format('HH:mm');
    } catch (error) {
      console.error("Error formatting time:", error);
      return '-';
    }
  };
  

  const getStatusTag = (status) => {
    if (status === 'completed') {
      return <Tag color="green">{t('completed')}</Tag>;
    } else if (status === 'cancelled') {
      return <Tag color="red">{t('cancelled')}</Tag>;
    }
    return <Tag color="blue">{status || t('planned')}</Tag>;
  };

  const generatePDF = async () => {
    if (!lessonPlan) return;

    try {
      setIsGeneratingPDF(true);
      message.loading({ content: t('generatingPDF'), key: 'pdfGeneration', duration: 0 });

      // Create a temporary element to render the PDF content
      const pdfContent = document.createElement('div');
      pdfContent.id = 'pdf-content';
      pdfContent.style.width = '794px'; // A4 width in pixels at 96 DPI
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '0';
      pdfContent.style.backgroundColor = 'white';
      pdfContent.style.padding = '40px';
      pdfContent.style.fontFamily = 'Arial, sans-serif';
      
      // Add content to the temporary element - using simple HTML for better PDF conversion
      pdfContent.innerHTML = `
        <div style="display: flex; justify-content: space-between; margin-bottom: 20px; align-items: center;">
          <div style="height: 60px;">
            <img src="/images/advisorlogo.png" alt="School Logo" style="height: 100%; max-width: 180px; object-fit: contain;" />
          </div>
          <div><strong>${t('Teacher')}:</strong> ${user ? ` ${user.nickname || ''} ${user.firstname || ''} ${user.lastname || ''}`.trim() : ''}</div>
        </div>
        
        <div style="text-align: center; margin-bottom: 20px;">
          <h1 style="font-size: 24px; margin-bottom: 8px;">${lessonPlan.title}</h1>
          <div>
            <span style="display: inline-block; padding: 2px 8px; background-color: #e6f7ff; color: #1890ff; border: 1px solid #91d5ff; border-radius: 2px; margin-right: 10px;">
              ${lessonPlan.status || t('planned')}
            </span>
            <span style="color: #8c8c8c;">ID: ${lessonPlan.id}</span>
          </div>
        </div>
        
        <div style="margin-bottom: 20px;">
          <!-- Modified table layout: one item per row for better text handling -->
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #d9d9d9;">
            <tr>
              <td style="border: 1px solid #d9d9d9; padding: 8px; width: 20%; background-color: #fafafa;"><strong>${t('classcode')}</strong></td>
              <td style="border: 1px solid #d9d9d9; padding: 8px; width: 80%;">${classInfo?.class_code || '-'}</td>
            </tr>
            <tr>
              <td style="border: 1px solid #d9d9d9; padding: 8px; background-color: #fafafa;"><strong>${t('subject')}</strong></td>
              <td style="border: 1px solid #d9d9d9; padding: 8px;">${classInfo?.subject_name || '-'}</td>
            </tr>
            <tr>
              <td style="border: 1px solid #d9d9d9; padding: 8px; background-color: #fafafa;"><strong>${t('date')}</strong></td>
              <td style="border: 1px solid #d9d9d9; padding: 8px;">${formatDate(lessonPlan.lesson_date)} ${t('at')} ${formatTime(lessonPlan.start_time)} - ${formatTime(lessonPlan.end_time)}</td>
            </tr>
          </table>
        </div>
        
        <hr style="border: none; border-top: 1px solid #d9d9d9; margin: 20px 0;" />
        
        <div style="margin-bottom: 20px;">
          <h2 style="font-size: 18px; margin-bottom: 10px;">${t('Lesson Brief')}</h2>
          <p>${lessonPlan.description || t('noDescriptionProvided')}</p>
        </div>
        
        <hr style="border: none; border-top: 1px solid #d9d9d9; margin: 20px 0;" />
        
        <div style="margin-bottom: 20px;">
          <h2 style="font-size: 18px; margin-bottom: 10px;">${t('Learning Objectives')}</h2>
          <p>${lessonPlan.learning_objectives || t('noObjectivesProvided')}</p>
        </div>
        
        <hr style="border: none; border-top: 1px solid #d9d9d9; margin: 20px 0;" />
        
        <div style="margin-bottom: 20px;">
          <h2 style="font-size: 18px; margin-bottom: 10px;">${t('Activities')}</h2>
          <p>${lessonPlan.activities || t('noActivitiesProvided')}</p>
        </div>
        
        <hr style="border: none; border-top: 1px solid #d9d9d9; margin: 20px 0;" />
        
        <div style="margin-bottom: 20px;">
          <h2 style="font-size: 18px; margin-bottom: 10px;">${t('Assessment')}</h2>
          <p>${lessonPlan.assessment || t('noAssessmentProvided')}</p>
        </div>
        
        <hr style="border: none; border-top: 1px solid #d9d9d9; margin: 20px 0;" />
        
        <div style="margin-bottom: 20px;">
          <h2 style="font-size: 18px; margin-bottom: 10px;">${t('Homework')}</h2>
          <p>${lessonPlan.homework || t('noHomeworkProvided')}</p>
        </div>
        
        <hr style="border: none; border-top: 1px solid #d9d9d9; margin: 20px 0;" />
        
        <div style="margin-bottom: 20px;">
          <h2 style="font-size: 18px; margin-bottom: 10px;">${t('Materials')}</h2>
          <p>${lessonPlan.materials || t('noMaterialsProvided')}</p>
        </div>
        
        ${lessonPlan.notes ? `
          <hr style="border: none; border-top: 1px solid #d9d9d9; margin: 20px 0;" />
          
          <div style="margin-bottom: 20px;">
            <h2 style="font-size: 18px; margin-bottom: 10px;">${t('notes')}</h2>
            <p>${lessonPlan.notes}</p>
          </div>
        ` : ''}
        
        <div style="margin-top: 30px; text-align: center; color: #8c8c8c; font-size: 10px">
          ${t('Printed')}: ${moment().format('DD MMM YYYY, HH:mm')}
        </div>
      `;
      
      // Append the element to the body
      document.body.appendChild(pdfContent);
      
      // Use html2canvas to create a canvas from the content
      const canvas = await html2canvas(pdfContent, {
        scale: 2, // Better quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Initialize PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate the required height to maintain aspect ratio
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add the canvas as an image to the PDF
      const imgData = canvas.toDataURL('image/png');
      
      // Add pages as needed
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add subsequent pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Save the PDF
      pdf.save(`Lesson_Plan_${lessonPlan.id || 'Detail'}.pdf`);
      
      // Clean up
      document.body.removeChild(pdfContent);
      
      message.success({ content: t('pdfGenerated'), key: 'pdfGeneration', duration: 2 });
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error({ content: t('pdfGenerationError'), key: 'pdfGeneration', duration: 3 });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!lessonPlan) return null;

  const teacherName = user ? ` ${user.nickname || ''} ${user.firstname || ''} ${user.lastname || ''}`.trim() : '';

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      width={isMobile ? '100%' : 800}
      style={{ top: isMobile ? 0 : 20 }}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('close')}
        </Button>,
        <Button 
          key="download" 
          type="primary" 
          icon={<FontAwesomeIcon icon={faFilePdf} />}
          onClick={generatePDF}
          loading={isGeneratingPDF}
        >
          {t('downloadPDF')}
        </Button>
      ]}
    >
      <div style={{ padding: isMobile ? '12px' : '20px' }}>
        {/* Top section with logo and teacher name */}
        <Row style={{ marginBottom: '20px' }} align="middle">
          {/* School Logo (top left) */}
          <Col xs={12} style={{ textAlign: 'left' }}>
            <img src="/images/advisorlogo.png" alt="School Logo" style={{ height: '40px', objectFit: 'contain' }} />
          </Col>
          
          {/* Teacher Name (top right) */}
          <Col xs={12} style={{ textAlign: 'right' }}>
            <Text strong>{t('Teacher')}:</Text> {teacherName}
          </Col>
        </Row>

        <div className="lesson-plan-header" style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Title level={isMobile ? 4 : 3}>{lessonPlan.title}</Title>
          <Space>
            {getStatusTag(lessonPlan.status)}
            <Text type="secondary">{`ID: ${lessonPlan.id}`}</Text>
          </Space>
        </div>

        {/* Modified to use one item per row */}
        <Descriptions
          bordered
          column={1}
          size={isMobile ? "small" : "default"}
          labelStyle={{ width: '20%' }}
          contentStyle={{ width: '80%' }}
          layout="horizontal"
        >
          <Descriptions.Item label={t('classcode')}>{classInfo?.class_code || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('subject')}>{classInfo?.subject_name || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('date')}>{formatDate(lessonPlan.lesson_date)} {t('at')} {formatTime(lessonPlan.start_time)} - {formatTime(lessonPlan.end_time)}</Descriptions.Item>
        </Descriptions>

        <Divider />

        <div className="lesson-plan-content">
          <div className="section">
            <Title level={5}>{t('Lesson Brief')}</Title>
            <Paragraph>{lessonPlan.description || t('noDescriptionProvided')}</Paragraph>
          </div>

          <Divider />

          <div className="section">
            <Title level={5}>{t('Learning Objectives')}</Title>
            <Paragraph>{lessonPlan.learning_objectives || t('noObjectivesProvided')}</Paragraph>
          </div>

          <Divider />

          <div className="section">
            <Title level={5}>{t('Activities')}</Title>
            <Paragraph>{lessonPlan.activities || t('noActivitiesProvided')}</Paragraph>
          </div>

          <Divider />

          <div className="section">
            <Title level={5}>{t('Assessment')}</Title>
            <Paragraph>{lessonPlan.assessment || t('noAssessmentProvided')}</Paragraph>
          </div>

          <Divider />

          <div className="section">
            <Title level={5}>{t('Homework')}</Title>
            <Paragraph>{lessonPlan.homework || t('noHomeworkProvided')}</Paragraph>
          </div>

          <Divider />

          <div className="section">
            <Title level={5}>{t('Materials')}</Title>
            <Paragraph>{lessonPlan.materials || t('noMaterialsProvided')}</Paragraph>
          </div>

          {lessonPlan.notes && (
            <>
              <Divider />
              <div className="section">
                <Title level={5}>{t('notes')}</Title>
                <Paragraph>{lessonPlan.notes}</Paragraph>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default withRoleAccess(LessonPlanView, ['superadmin', 'admin', 'teacher']);